import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Drag the rows of one release date into a new order (Amanda's note
// 2026-09-07; PR team ruling 2026-09-08: the rows themselves, with a
// handle, like the DPK Albums page, no popup). Lives on the day's <tbody>,
// so a row can never leave its date. Drop = ONE PATCH to the reorder_day
// door carrying the page's own query (tab, status, client, search, month,
// dir), so the server validates and re-renders inside the scope the page
// is showing; its turbo_stream response swaps this <tbody> in place. A
// failed save puts the rows back where they were and says so.
export default class extends Controller {
  static values = { url: String, date: String }

  connect() {
    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle: "[data-day-sort-handle]",
      draggable: "tr[data-release-id]",
      ghostClass: "opacity-30",
      // Autoscroll like the DPK Albums page (the team's reference):
      // native HTML5 autoscroll stalls the moment the pointer is held
      // still at the edge, so run Sortable's own loop.
      scroll: true,
      forceAutoScrollFallback: true,
      scrollSensitivity: 80,
      scrollSpeed: 12,
      bubbleScroll: true,
      // Touch: press-and-hold to drag so a quick swipe still scrolls; the
      // touch ghost goes on the body, never inside the tbody, so the
      // rail's first/last-row CSS never sees a duplicate row mid-drag.
      delay: 150,
      delayOnTouchOnly: true,
      fallbackOnBody: true,
      onEnd: this.onDragEnd.bind(this)
    })
  }

  disconnect() {
    this.sortable?.destroy()
  }

  onDragEnd(event) {
    if (event.oldDraggableIndex === event.newDraggableIndex) return

    const rows = Array.from(this.element.querySelectorAll("tr[data-release-id]"))
    const ids = rows.map((row) => row.dataset.releaseId)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const pageQuery = Object.fromEntries(new URLSearchParams(window.location.search))
    // One save at a time: a second drag before the first answers could
    // land its stream out of order.
    this.sortable.option("disabled", true)

    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({
        ...pageQuery,
        date: this.dateValue,
        ids: ids,
        last_group: this.element.nextElementSibling === null
      })
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((stream) => window.Turbo.renderStreamMessage(stream))
      .catch(() => {
        // Put the row back where it came from; nothing was saved.
        const moved = rows[event.newDraggableIndex]
        const before = this.element.querySelectorAll("tr[data-release-id]")[event.oldDraggableIndex]
        if (moved && before && moved !== before) {
          event.newDraggableIndex < event.oldDraggableIndex ? before.after(moved) : before.before(moved)
        }
        if (window.showToast) window.showToast("error", "Could not save the new order. Nothing was changed.")
      })
      .finally(() => this.sortable?.option("disabled", false))
  }
}
